import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, List, ListItemButton,
  ListItemIcon, ListItemText, Typography, Box,
  IconButton, CircularProgress, Chip, alpha, Stack, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { sortCategoriesHierarchically } from '../utils/category';

export default function QuickCategorySelectDialog({
  open,
  onClose,
  currentCategoryId = null,
  categories = [],
  onSelectCategory,
  isSaving = false,
  title = null,
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Reset search when dialog opens
  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  // Hierarchically sorted categories with search filter
  const filteredCategories = useMemo(() => {
    const sorted = sortCategoriesHierarchically(categories, t);
    if (!search.trim()) return sorted;

    const query = search.trim().toLowerCase();
    return sorted.filter(cat => {
      const name = (cat.name || '').toLowerCase();
      const translatedName = (t(cat.name) || '').toLowerCase();
      return name.includes(query) || translatedName.includes(query);
    });
  }, [categories, search, t]);

  const handleSelect = (category) => {
    if (isSaving) return;
    onSelectCategory(category);
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {title || t('Change Category')}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={isSaving}
          aria-label="close"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('Search categories...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={isSaving}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: {
              borderRadius: 2,
              bgcolor: 'action.hover',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'divider' },
            }
          }}
        />
      </Box>

      <DialogContent dividers sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
        {isSaving ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              {t('Category updated successfully')}...
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ py: 1 }}>
            {/* Uncategorized Option */}
            <ListItemButton
              onClick={() => handleSelect(null)}
              selected={currentCategoryId === null || currentCategoryId === '' || currentCategoryId === undefined}
              sx={{
                py: 1.25,
                px: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&.Mui-selected': {
                  bgcolor: alpha('#1E4D2B', 0.08),
                  fontWeight: 700,
                  '&:hover': { bgcolor: alpha('#1E4D2B', 0.12) }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BlockIcon fontSize="small" sx={{ color: 'text.secondary', opacity: 0.7 }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontWeight: currentCategoryId == null ? 700 : 500 }}>
                    {t('Uncategorized')}
                  </Typography>
                }
              />
              {(currentCategoryId === null || currentCategoryId === '' || currentCategoryId === undefined) && (
                <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
              )}
            </ListItemButton>

            {/* Filtered Category Items */}
            {filteredCategories.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('No data available')}
                </Typography>
              </Box>
            ) : (
              filteredCategories.map((cat) => {
                const isSub = Boolean(cat.parent_id);
                const isSelected = String(currentCategoryId) === String(cat.id);
                const catColor = cat.color || '#1E4D2B';

                return (
                  <ListItemButton
                    key={cat.id}
                    onClick={() => handleSelect(cat)}
                    selected={isSelected}
                    sx={{
                      py: 1,
                      pl: isSub ? 5.5 : 3,
                      pr: 3,
                      '&.Mui-selected': {
                        bgcolor: alpha(catColor, 0.1),
                        fontWeight: 700,
                        '&:hover': { bgcolor: alpha(catColor, 0.15) }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box
                        sx={{
                          width: isSub ? 8 : 12,
                          height: isSub ? 8 : 12,
                          borderRadius: '50%',
                          bgcolor: catColor,
                          opacity: isSub ? 0.8 : 1,
                          mr: 1
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 700 : (isSub ? 500 : 600),
                              color: isSelected ? 'text.primary' : (isSub ? 'text.secondary' : 'text.primary')
                            }}
                          >
                            {t(cat.name)}
                          </Typography>
                          {!isSub && cat.category_type && (
                            <Chip
                              size="small"
                              label={cat.category_type}
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                textTransform: 'capitalize',
                                bgcolor: alpha(catColor, 0.08),
                                color: catColor,
                                fontWeight: 700
                              }}
                            />
                          )}
                        </Stack>
                      }
                    />
                    {isSelected && (
                      <CheckIcon fontSize="small" sx={{ color: catColor }} />
                    )}
                  </ListItemButton>
                );
              })
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: 'background.paper' }}>
        <Button onClick={onClose} disabled={isSaving} color="inherit" sx={{ fontWeight: 600, textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
